import * as React from 'react';
import moment from 'moment';
import DateTime from 'react-datetime';
import * as actions from './MultiplePhotographersInvoiceActions';
import * as singlePhotographerActions from '../photographer/SinglePhotographerInvoiceActions';
import * as Features from '../../../../shared/Features';
import { connect } from 'react-redux';
import { Translate } from '../../../../shared/LanguageService';
import { IPhotographerInvoiceCollection } from './MultiplePhotographersInvoiceTypes';
import { handleError } from '../../../../shared/ErrorCatching';
import { IPhotographerInvoice } from '../photographer/SinglePhotographerInvoiceTypes';
import { Row, Col } from 'react-bootstrap';
import ViewPhotographersInvoiceModal from './ViewPhotographersInvoiceModal';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import InvoiceCollectionListRow from './InvoiceCollectionListRow';
import { Outlier } from './outliers/OutlierRow';
import { PhotographerCost } from './outliers/CostCheck';

interface IState {
    showPreviewModal: boolean;
    showPendingModal: boolean;
    showHistoryModal: boolean;
    working: boolean;
    showCreateFileAgainConfirmation: boolean;
    showCreateUnapprovedInvoiceConfirmation: boolean;
    collection: IPhotographerInvoiceCollection;

    showRejectedInvoiceModal: boolean;
    rejectedInvoice: IPhotographerInvoice;
    rejectedInvoiceWorking: boolean;

    when?: Date;
}

interface IActions {
    clearState: () => Promise<any>;
    getLists: () => Promise<any>;
    getInvoiceCollection: (type: string) => Promise<any>;
    getHistoricInvoiceCollection: (photographerInvoiceCollectionId: number) => Promise<any>;
    savePendingInvoiceCollection: (checksums: any) => Promise<any>;
    createPaymentFile: (photographerInvoiceCollectionId: number, when?: Date) => Promise<any>;
    createPaymentFileV2: (photographerInvoiceCollectionId: number, when?: Date) => Promise<any>;

    getRejectedInvoice: (invoice: IPhotographerInvoice) => Promise<any>;
    unlockRejectedInvoice: (invoice: IPhotographerInvoice) => Promise<any>;
}

interface IProps {
    pendingInvoiceCollections: IPhotographerInvoiceCollection[];
    previewInvoiceCollections: IPhotographerInvoiceCollection[];
    invoiceCollectionHistory: IPhotographerInvoiceCollection[];
    html: string;
    checksums: any;
    user: any;
    outliers: Outlier[];
    topTenCosts: PhotographerCost[];
    rejections: IPhotographerInvoice[];
    exclusions: IPhotographerInvoice[];
    passivePhotographers: string[];
    rejectedHtml: string;
    rejectMessage: string;

    actions: IActions;
}

class MultiplePhotographersInvoicePage extends React.Component<IProps, IState> {
    constructor(props: any, context: any) {
        super(props, context);

        this.state = {
            showPreviewModal: false,
            showPendingModal: false,
            showHistoryModal: false,
            working: false,
            showCreateFileAgainConfirmation: false,
            showCreateUnapprovedInvoiceConfirmation: false,
            collection: null,

            showRejectedInvoiceModal: false,
            rejectedInvoice: null,
            rejectedInvoiceWorking: false,

            when: null
        };

        this.showPendingInvoiceCollection = this.showPendingInvoiceCollection.bind(this);
        this.showPreviewInvoiceCollection = this.showPreviewInvoiceCollection.bind(this);
        this.showHistoricInvoiceCollection = this.showHistoricInvoiceCollection.bind(this);
        this.savePendingCollection = this.savePendingCollection.bind(this);
        this.downloadPaymentFile = this.downloadPaymentFile.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.onWhenChange = this.onWhenChange.bind(this);

        this.showRejectedInvoiceModal = this.showRejectedInvoiceModal.bind(this);
        this.closeRejectionModal = this.closeRejectionModal.bind(this);
        this.unlockRejectedInvoice = this.unlockRejectedInvoice.bind(this);
    }

    componentDidMount(): void {
        this.props.actions.getLists().catch((error: any) => handleError(error));
    }

    componentWillUnmount(): void {
        this.props.actions.clearState();
    }

    showPendingInvoiceCollection(collection: IPhotographerInvoiceCollection): void {
        this.setState({ collection: collection } as IState);
        this.props.actions
            .getInvoiceCollection('pending')
            .then(() => {
                this.setState({
                    showPreviewModal: false,
                    showPendingModal: true,
                    showHistoryModal: false,
                    working: false,
                    showCreateFileAgainConfirmation: false,
                    showCreateUnapprovedInvoiceConfirmation: false
                } as IState);
            })
            .catch((error: any) => handleError(error));
    }

    showPreviewInvoiceCollection(): void {
        this.props.actions
            .getInvoiceCollection('preview')
            .then(() => {
                this.setState({
                    showPreviewModal: true,
                    showPendingModal: false,
                    showHistoryModal: false,
                    working: false,
                    showCreateFileAgainConfirmation: false,
                    showCreateUnapprovedInvoiceConfirmation: false,
                    collection: null
                } as IState);
            })
            .catch((error: any) => handleError(error));
    }

    showHistoricInvoiceCollection(collection: IPhotographerInvoiceCollection): void {
        this.props.actions
            .getHistoricInvoiceCollection(collection.id)
            .then(() => {
                this.setState({
                    showPreviewModal: false,
                    showPendingModal: false,
                    showHistoryModal: true,
                    working: false,
                    showCreateFileAgainConfirmation: false,
                    showCreateUnapprovedInvoiceConfirmation: false,
                    collection: null
                } as IState);
            })
            .catch((error: any) => handleError(error));
    }

    savePendingCollection(
        collection: IPhotographerInvoiceCollection,
        shouldForceIt: boolean
    ): void {
        this.setState({ working: true } as IState);

        if (collection.hasAnyUnhandledInvoices && !shouldForceIt) {
            this.setState({
                showCreateUnapprovedInvoiceConfirmation: true,
                collection: collection
            } as IState);
        } else {
            this.props.actions
                .savePendingInvoiceCollection(this.props.checksums)
                .then(() => {
                    this.setState({
                        showPreviewModal: false,
                        showPendingModal: false,
                        working: false,
                        showCreateFileAgainConfirmation: false,
                        showCreateUnapprovedInvoiceConfirmation: false,
                        collection: null
                    } as IState);
                })
                .catch((error: any) => {
                    this.setState({ working: false } as IState);
                    handleError(error);
                });
        }
    }

    downloadPaymentFile(collection: IPhotographerInvoiceCollection, shouldForceIt: boolean): void {
        if (collection.fileHasBeenCreated && !shouldForceIt) {
            this.setState({
                showCreateFileAgainConfirmation: true,
                collection: collection
            } as IState);
        } else {
            this.props.actions
                .createPaymentFile(collection.id, this.state.when)
                .then(() =>
                    this.props.actions
                        .getLists()
                        .then(() =>
                            this.setState({
                                showCreateFileAgainConfirmation: false,
                                collection: null
                            } as IState)
                        )
                        .catch((error: any) => handleError(error))
                )
                .catch((error: any) => handleError(error));
        }
    }

    downloadPaymentFileV2(collection: IPhotographerInvoiceCollection): void {
        this.props.actions
            .createPaymentFileV2(collection.id, this.state.when)
            .then(() =>
                this.props.actions
                    .getLists()
                    .then(() =>
                        this.setState({
                            showCreateFileAgainConfirmation: false,
                            collection: null
                        } as IState)
                    )
                    .catch((error: any) => handleError(error))
            )
            .catch((error: any) => handleError(error));
    }

    closeModal() {
        this.setState({
            showPreviewModal: false,
            showPendingModal: false,
            showHistoryModal: false,
            working: false,
            showCreateFileAgainConfirmation: false,
            showCreateUnapprovedInvoiceConfirmation: false,
            collection: null
        } as IState);
    }

    onWhenChange(e: any): void {
        let value = e ? e.toDate() : null;
        this.setState({ when: value } as IState);
    }

    showRejectedInvoiceModal(rejectedInvoice: IPhotographerInvoice): void {
        this.setState({ rejectedInvoice: rejectedInvoice } as IState);
        this.props.actions
            .getRejectedInvoice(rejectedInvoice)
            .then(() => {
                this.setState({
                    showRejectedInvoiceModal: true,
                    rejectedInvoiceWorking: false
                } as IState);
            })
            .catch((error: any) => handleError(error));
    }

    unlockRejectedInvoice(): void {
        this.props.actions
            .unlockRejectedInvoice(this.state.rejectedInvoice)
            .then(() =>
                this.props.actions
                    .getLists()
                    .then(() =>
                        this.props.actions
                            .getInvoiceCollection('pending')
                            .then(() => this.closeRejectionModal())
                    )
            )
            .catch((error: any) => handleError(error));
    }

    closeRejectionModal() {
        this.setState({
            showRejectedInvoiceModal: false,
            rejectedInvoice: null,
            rejectedInvoiceWorking: false
        } as IState);
    }

    render() {
        return (
            <div>
                <h1>{Translate('photographer-invoice-collections/title')}</h1>
                <fieldset>
                    <h2>{Translate('photographer-invoice-collections/lists/pending/header')}</h2>
                    {this.props.pendingInvoiceCollections.map(
                        (x: IPhotographerInvoiceCollection, index: number) => (
                            <InvoiceCollectionListRow
                                key={index}
                                invoiceCollection={x}
                                anyRejections={x.hasAnyRejectedInvoices}
                                anyUnhandled={x.hasAnyUnhandledInvoices}
                                view={(collection: IPhotographerInvoiceCollection) =>
                                    this.showPendingInvoiceCollection(collection)
                                }
                                canCreateFile={false}
                                download={() => {}}
                                downloadV2={() => {}}
                            />
                        )
                    )}

                    <h2>{Translate('photographer-invoice-collections/lists/previews/header')}</h2>
                    {this.props.previewInvoiceCollections.map(
                        (x: IPhotographerInvoiceCollection, index: number) => (
                            <InvoiceCollectionListRow
                                key={index}
                                invoiceCollection={x}
                                anyRejections={false}
                                anyUnhandled={false}
                                view={() => this.showPreviewInvoiceCollection()}
                                canCreateFile={false}
                                download={() => {}}
                                downloadV2={() => {}}
                            />
                        )
                    )}
                    <Row>
                        <Col xs={6}>
                            <h2>
                                {Translate('photographer-invoice-collections/lists/history/header')}
                            </h2>
                        </Col>
                        <Col xs={6} md={2} className="photographer-invoice-payment-date">
                            <DateTime
                                inputProps={{
                                    placeholder: Translate(
                                        'photographer-invoice-collections/lists/history/labels/when'
                                    )
                                }}
                                locale={moment.locale()}
                                input={true}
                                onChange={this.onWhenChange}
                                timeFormat={false}
                                value={this.state.when}
                                closeOnSelect={true}
                            />
                        </Col>
                    </Row>
                    {this.props.invoiceCollectionHistory.map(
                        (x: IPhotographerInvoiceCollection, index: number) => (
                            <InvoiceCollectionListRow
                                key={index}
                                invoiceCollection={x}
                                anyRejections={false}
                                anyUnhandled={false}
                                view={(collection: IPhotographerInvoiceCollection) =>
                                    this.showHistoricInvoiceCollection(collection)
                                }
                                canCreateFile={Features.hasFeature(
                                    Features.GeneratePhotographerInvoiceCollectionFiles,
                                    this.props.user
                                )}
                                download={(collection: IPhotographerInvoiceCollection) =>
                                    this.downloadPaymentFile(collection, false)
                                }
                                downloadV2={(collection: IPhotographerInvoiceCollection) =>
                                    this.downloadPaymentFileV2(collection)
                                }                                
                            />
                        )
                    )}

                    {this.state.showPreviewModal && (
                        <ViewPhotographersInvoiceModal
                            html={this.props.html}
                            closeModal={this.closeModal}
                            allowSaving={false}
                            save={() => this.savePendingCollection(this.state.collection, false)}
                            working={this.state.working}
                            closeRejectionModal={undefined}
                            rejectedHtml={undefined}
                            rejectedInvoice={undefined}
                            rejectedInvoiceWorking={undefined}
                            rejections={undefined}
                            exclusions={undefined}
                            passivePhotographers={undefined}
                            rejectMessage={undefined}
                            showInvoice={undefined}
                            showModal={undefined}
                            unlockRejectedInvoice={undefined}
                            updateRejectedInvoice={undefined}
                        />
                    )}

                    {this.state.showPendingModal && (
                        <ViewPhotographersInvoiceModal
                            html={this.props.html}
                            outliers={this.props.outliers}
                            topTenCosts={this.props.topTenCosts}
                            closeModal={() => this.closeModal()}
                            allowSaving={true}
                            save={() => this.savePendingCollection(this.state.collection, false)}
                            working={this.state.working}
                            closeRejectionModal={this.closeRejectionModal}
                            rejectedHtml={this.props.rejectedHtml}
                            rejectedInvoice={this.state.rejectedInvoice}
                            rejectedInvoiceWorking={this.state.rejectedInvoiceWorking}
                            rejections={this.props.rejections}
                            exclusions={this.props.exclusions}
                            passivePhotographers={this.props.passivePhotographers}
                            rejectMessage={this.props.rejectMessage}
                            showInvoice={(rejectedInvoice: IPhotographerInvoice) =>
                                this.showRejectedInvoiceModal(rejectedInvoice)
                            }
                            showModal={this.state.showRejectedInvoiceModal}
                            unlockRejectedInvoice={this.unlockRejectedInvoice}
                            updateRejectedInvoice={(rejectedInvoice: IPhotographerInvoice) =>
                                this.props.actions
                                    .getRejectedInvoice(rejectedInvoice)
                                    .catch((error: any) => handleError(error))
                            }
                        />
                    )}

                    {this.state.showHistoryModal && (
                        <ViewPhotographersInvoiceModal
                            html={this.props.html}
                            closeModal={this.closeModal}
                            allowSaving={false}
                            save={() => this.savePendingCollection(this.state.collection, false)}
                            working={this.state.working}
                            closeRejectionModal={undefined}
                            rejectedHtml={undefined}
                            rejectedInvoice={undefined}
                            rejectedInvoiceWorking={undefined}
                            rejections={undefined}
                            exclusions={this.props.exclusions}
                            passivePhotographers={undefined}
                            rejectMessage={undefined}
                            showInvoice={undefined}
                            showModal={undefined}
                            unlockRejectedInvoice={undefined}
                            updateRejectedInvoice={undefined}
                        />
                    )}

                    {this.state.showCreateUnapprovedInvoiceConfirmation && (
                        <ConfirmationModal
                            title={Translate(
                                'photographer-invoice-collections/modal/save/confirmation-dialog/title'
                            )}
                            text={Translate(
                                'photographer-invoice-collections/modal/save/confirmation-dialog/text'
                            )}
                            doButtonLabel={Translate(
                                'photographer-invoice-collections/modal/save/confirmation-dialog/button-labels/do'
                            )}
                            cancelButtonLabel={Translate(
                                'photographer-invoice-collections/modal/save/confirmation-dialog/button-labels/cancel'
                            )}
                            doButtonStyle="danger"
                            backdrop="static"
                            do={() => {
                                this.savePendingCollection(this.state.collection, true);
                                this.closeModal();
                            }}
                            noAction={() => this.setState({ working: false } as IState)}
                            closeModal={() =>
                                this.setState({
                                    showCreateUnapprovedInvoiceConfirmation: false
                                } as IState)
                            }
                        />
                    )}

                    {this.state.showCreateFileAgainConfirmation && (
                        <ConfirmationModal
                            title={Translate(
                                'photographer-invoice-collections/payment-file/confirmation-dialog/title'
                            )}
                            text={Translate(
                                'photographer-invoice-collections/payment-file/confirmation-dialog/text'
                            )}
                            doButtonLabel={Translate(
                                'photographer-invoice-collections/payment-file/confirmation-dialog/button-labels/do'
                            )}
                            cancelButtonLabel={Translate(
                                'photographer-invoice-collections/payment-file/confirmation-dialog/button-labels/cancel'
                            )}
                            doButtonStyle="danger"
                            do={() => this.downloadPaymentFile(this.state.collection, true)}
                            closeModal={() =>
                                this.setState({
                                    showCreateFileAgainConfirmation: false,
                                    working: false
                                } as IState)
                            }
                        />
                    )}
                </fieldset>
            </div>
        );
    }
}

function mapStateToProps(state: any) {
    return {
        pendingInvoiceCollections: state.multiplePhotographersInvoice.pendingInvoiceCollections,
        previewInvoiceCollections: state.multiplePhotographersInvoice.previewInvoiceCollections,
        invoiceCollectionHistory: state.multiplePhotographersInvoice.invoiceCollectionHistory,
        html: state.multiplePhotographersInvoice.html,
        checksums: state.multiplePhotographersInvoice.checksums,
        outliers: state.multiplePhotographersInvoice.outliers,
        topTenCosts: state.multiplePhotographersInvoice.topTenCosts,
        user: state.user,

        rejections: state.multiplePhotographersInvoice.rejections,
        exclusions: state.multiplePhotographersInvoice.exclusions,
        passivePhotographers: state.multiplePhotographersInvoice.passivePhotographers,
        rejectedHtml: state.singlePhotographerInvoice.html,
        rejectMessage: state.singlePhotographerInvoice.rejectMessage
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getLists: () => dispatch(actions.getLists()),
            clearState: () => dispatch(actions.clearState()),
            getInvoiceCollection: (type: string) => dispatch(actions.getInvoiceCollection(type)),
            getHistoricInvoiceCollection: (photographerInvoiceCollectionId: number) =>
                dispatch(actions.getHistoricInvoiceCollection(photographerInvoiceCollectionId)),
            savePendingInvoiceCollection: (checksums: any) =>
                dispatch(actions.savePendingInvoiceCollection(checksums)),
            createPaymentFile: (collectionId: number, when?: Date) =>
                dispatch(actions.createPaymentFile(collectionId, when)),
            createPaymentFileV2: (collectionId: number, when?: Date) =>
                dispatch(actions.createPaymentFileV2(collectionId, when)),

            getRejectedInvoice: (invoice: IPhotographerInvoice) =>
                dispatch(
                    singlePhotographerActions.getRejectedInvoice(
                        invoice.photographerId,
                        invoice.year,
                        invoice.month,
                        invoice.day
                    )
                ),
            unlockRejectedInvoice: (invoice: IPhotographerInvoice) =>
                dispatch(
                    singlePhotographerActions.unlockRejectedInvoice(
                        invoice.photographerId,
                        invoice.year,
                        invoice.month,
                        invoice.day
                    )
                )
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MultiplePhotographersInvoicePage);
